import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Select } from 'antd';
import { Search, X } from 'lucide-react';
import useDebounce from '@/hooks/useDebounce';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import type { DataTableFilter, DataTableToolbarConfig } from './types';

type Props = DataTableToolbarConfig & {
  primaryAction?: { label: string; icon?: ReactNode; onClick: () => void };
  secondaryActions?: ReactNode;
};

function SearchBox({ search }: { search: NonNullable<DataTableToolbarConfig['search']> }) {
  const [text, setText] = useState(search.value);
  const debounced = useDebounce(text, search.debounceMs ?? 250);

  useEffect(() => {
    if (debounced !== search.value) search.onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8A82]" />
      <input
        aria-label="Search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={search.placeholder ?? 'Search…'}
        className="h-10 w-full sm:w-80 rounded-xl pl-9 pr-3 text-sm outline-none transition-colors"
        style={{
          background: SOVEREIGN.inputBg,
          border: `1px solid ${SOVEREIGN.inputBorder}`,
          color: SOVEREIGN.title,
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = SOVEREIGN.title;
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(166,206,57,0.22)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = SOVEREIGN.inputBorder;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
    </div>
  );
}

function FilterControl({ filter }: { filter: DataTableFilter }) {
  const multiple = filter.mode === 'multiple';
  return (
    <Select
      aria-label={filter.label}
      mode={multiple ? 'multiple' : undefined}
      allowClear
      maxTagCount="responsive"
      placeholder={filter.label}
      value={(filter.value as string | string[] | undefined) ?? (multiple ? [] : undefined)}
      onChange={(v) => filter.onChange((v ?? (multiple ? [] : null)) as string | string[] | null)}
      options={filter.options}
      style={{ minWidth: 168 }}
    />
  );
}

export default function DataTableToolbar({
  search,
  filters,
  activeFilters,
  onClearFilter,
  onClearAll,
  primaryAction,
  secondaryActions,
}: Props) {
  const hasChips = !!activeFilters?.length;

  return (
    <div className="space-y-3">
      {/* Top row: search + filters | actions */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {search && <SearchBox search={search} />}
          {filters?.map((f) => <FilterControl key={f.key} filter={f} />)}
        </div>

        <div className="flex items-center gap-2">
          {secondaryActions}
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-colors"
              style={{
                background: SOVEREIGN.btn,
                border: `1px solid ${SOVEREIGN.btnBorder}`,
                boxShadow: SOVEREIGN.btnShadow,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = SOVEREIGN.btnHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = SOVEREIGN.btn;
              }}
            >
              {primaryAction.icon}
              {primaryAction.label}
            </button>
          )}
        </div>
      </div>

      {/* Active-filter chips row */}
      {hasChips && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters!.map((chip) => (
            <span
              key={`${chip.key}:${chip.value}`}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ background: 'rgba(201,169,97,0.14)', color: SOVEREIGN.ink }}
            >
              {chip.display}
              <button
                type="button"
                aria-label={`remove ${chip.display}`}
                onClick={() => onClearFilter?.(chip)}
                className="rounded-full p-0.5 hover:bg-black/5"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs font-semibold underline-offset-2 hover:underline"
            style={{ color: SOVEREIGN.bottle }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
